import {
  fieldTitle,
  literalFieldLabels,
  resultLabel,
} from "@/lib/localized-text";
import { tx } from "@/lib/i18n";
import { useLocale } from "@/lib/i18n-react";
import type { ReactNode } from "react";
import type {
  FieldFormat,
  QueryResult,
  VisualizationConfig,
  VisualizationType,
} from "@/lib/types";
import {
  CHART_COLORS,
  FIELD_FORMATS,
  kpiPreset,
} from "@/lib/dashboard-widgets";
import { label } from "@/lib/format";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { SelectField } from "./ui/select";

export function Setting({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  useLocale();
  return (
    <label className="flex min-w-0 flex-col gap-1.5 text-xs font-medium">
      {title}
      {children}
    </label>
  );
}
export function VisualizationSettings({
  config,
  data,
  onChange,
}: {
  config: VisualizationConfig;
  data: QueryResult;
  onChange: (config: VisualizationConfig) => void;
}) {
  useLocale();
  const set = (patch: Partial<VisualizationConfig>) =>
    onChange({ ...config, ...patch });
  const literals = literalFieldLabels(config, data.fields);
  const numeric = data.fields.filter((f) => f.type === "number");
  const columns = config.columns ?? data.columns;
  const isChart = ["bar", "line", "pie"].includes(config.type);
  const moveColumn = (index: number, direction: number) => {
    const next = index + direction;
    if (next < 0 || next >= columns.length) return;
    const reordered = [...columns];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    set({ columns: reordered });
  };
  const fieldNames =
    config.type === "table"
      ? data.columns
      : [...new Set([...(isChart ? [config.x] : []), ...config.y])].filter(
          Boolean,
        );
  const appearance = config.kpi ?? {
    ...kpiPreset("neutral"),
    alignment: "center" as const,
    labelPosition: "below" as const,
    valueSize: "large" as const,
  };
  return (
    <div className="space-y-5">
      <Setting title={tx("Visualization")}>
        <SelectField
          aria-label={tx("Widget visualization")}
          value={config.type}
          onValueChange={(value) => {
            const type = value as VisualizationType;
            set({
              type,
              ...(type === "kpi" || type === "pie"
                ? {
                    y: config.y.length
                      ? config.y.slice(0, 1)
                      : numeric.slice(0, 1).map((f) => f.name),
                  }
                : {}),
            });
          }}
        >
          {["kpi", "table", "bar", "line", "pie"].map((type) => (
            <option key={type} value={type}>
              {label(type)}
            </option>
          ))}
        </SelectField>
      </Setting>
      <fieldset className="flex flex-wrap gap-4">
        <legend className="mb-2 text-xs font-semibold">
          {tx("Card details")}
        </legend>
        {(
          [
            ["showHeader", tx("Show heading")],
            ["showDescription", tx("Show description")],
            ["showFooter", tx("Show query footer")],
          ] as const
        ).map(([key, title]) => (
          <label key={key} className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={config[key] !== false}
              onChange={(e) => set({ [key]: e.target.checked })}
            />
            {tx(title)}
          </label>
        ))}
      </fieldset>
      {isChart && (
        <Setting title={tx("X-axis")}>
          <SelectField
            aria-label={tx("Widget X-axis")}
            value={config.x}
            onValueChange={(x) => set({ x })}
          >
            <option value="">{tx("Choose field")}</option>
            {data.fields
              .filter((f) => !f.fields)
              .map((f) => (
                <option key={f.name} value={f.name}>
                  {resultLabel(f)}
                </option>
              ))}
          </SelectField>
        </Setting>
      )}
      {config.type !== "table" && (
        <fieldset>
          <legend className="mb-2 text-xs font-semibold">
            {config.type === "kpi" ? tx("KPI value") : tx("Values")}
          </legend>
          <div className="flex flex-wrap gap-2">
            {numeric.map((f) => (
              <label
                key={f.name}
                className="flex items-center gap-2 rounded border p-2 text-xs"
              >
                <input
                  type={
                    config.type === "kpi" || config.type === "pie"
                      ? "radio"
                      : "checkbox"
                  }
                  name="widget-value"
                  checked={config.y.includes(f.name)}
                  onChange={(e) =>
                    set({
                      y:
                        config.type === "kpi" || config.type === "pie"
                          ? [f.name]
                          : e.target.checked
                            ? [...config.y, f.name]
                            : config.y.filter((y) => y !== f.name),
                    })
                  }
                />
                {resultLabel(f)}
              </label>
            ))}
          </div>
        </fieldset>
      )}
      {config.type === "kpi" && (
        <fieldset className="space-y-3">
          <legend className="mb-2 text-xs font-semibold">
            {tx("KPI appearance")}
          </legend>
          <Setting title={tx("Preset")}>
            <SelectField
              aria-label={tx("KPI preset")}
              value={config.kpi?.preset ?? ""}
              onValueChange={(preset) =>
                set({
                  kpi: kpiPreset(preset as "neutral" | "highlighted"),
                  showHeader: false,
                  showDescription: false,
                  showFooter: false,
                })
              }
            >
              <option value="">{tx("Choose preset")}</option>
              <option value="neutral">{tx("Neutral")}</option>
              <option value="highlighted">{tx("Highlighted teal")}</option>
            </SelectField>
          </Setting>
          <div className="grid gap-3 sm:grid-cols-3">
            <Setting title={tx("Alignment")}>
              <SelectField
                aria-label={tx("KPI alignment")}
                value={appearance.alignment}
                onValueChange={(alignment) =>
                  set({
                    kpi: {
                      ...appearance,
                      alignment: alignment as typeof appearance.alignment,
                    },
                  })
                }
              >
                {["start", "center", "end"].map((v) => (
                  <option key={v} value={v}>
                    {label(v)}
                  </option>
                ))}
              </SelectField>
            </Setting>
            <Setting title={tx("Label placement")}>
              <SelectField
                aria-label={tx("KPI label placement")}
                value={appearance.labelPosition}
                onValueChange={(labelPosition) =>
                  set({
                    kpi: {
                      ...appearance,
                      labelPosition:
                        labelPosition as typeof appearance.labelPosition,
                    },
                  })
                }
              >
                <option value="above">{tx("Above value")}</option>
                <option value="below">{tx("Below value")}</option>
              </SelectField>
            </Setting>
            <Setting title={tx("Value size")}>
              <SelectField
                aria-label={tx("KPI value size")}
                value={appearance.valueSize}
                onValueChange={(valueSize) =>
                  set({
                    kpi: {
                      ...appearance,
                      valueSize: valueSize as typeof appearance.valueSize,
                    },
                  })
                }
              >
                {["small", "medium", "large"].map((v) => (
                  <option key={v} value={v}>
                    {label(v)}
                  </option>
                ))}
              </SelectField>
            </Setting>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(
              [
                ["backgroundColor", tx("Surface color")],
                ["borderColor", tx("Border color")],
                ["valueColor", tx("Value color")],
              ] as const
            ).map(([key, title]) => (
              <Setting key={key} title={tx(title)}>
                <Input
                  type="color"
                  aria-label={tx(title)}
                  value={appearance[key]}
                  onChange={(e) =>
                    set({ kpi: { ...appearance, [key]: e.target.value } })
                  }
                />
              </Setting>
            ))}
          </div>
        </fieldset>
      )}
      {isChart && config.type !== "pie" && (
        <Setting title={tx("Target line (optional)")}>
          <Input
            aria-label={tx("Widget target")}
            type="number"
            value={config.target ?? ""}
            onChange={(e) =>
              set({
                target: e.target.value === "" ? null : e.target.valueAsNumber,
              })
            }
          />
        </Setting>
      )}
      {config.type === "table" && (
        <fieldset>
          <legend className="mb-2 text-xs font-semibold">
            {tx("Visible columns and order")}
          </legend>
          <div className="space-y-1">
            {[
              ...columns,
              ...data.columns.filter((c) => !columns.includes(c)),
            ].map((column) => {
              const index = columns.indexOf(column);
              return (
                <div
                  key={column}
                  className="flex items-center gap-2 rounded border px-2 py-1"
                >
                  <label className="flex min-w-0 flex-1 items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={index >= 0}
                      onChange={(e) =>
                        set({
                          columns: e.target.checked
                            ? [...columns, column]
                            : columns.filter((c) => c !== column),
                        })
                      }
                    />
                    {fieldTitle(column, data.fields)}
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index <= 0}
                    aria-label={tx("Move column {p0} up", { p0: column })}
                    onClick={() => moveColumn(index, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={index < 0 || index === columns.length - 1}
                    aria-label={tx("Move column {p0} down", { p0: column })}
                    onClick={() => moveColumn(index, 1)}
                  >
                    ↓
                  </Button>
                </div>
              );
            })}
          </div>
        </fieldset>
      )}
      <fieldset className="space-y-3">
        <legend className="mb-2 text-xs font-semibold">
          {tx("Field labels and formats")}
        </legend>
        {fieldNames.map((name) => {
          const field = data.fields.find((f) => f.name === name);
          return (
            <div key={name} className="grid gap-2 sm:grid-cols-2">
              <Setting title={fieldTitle(name, data.fields)}>
                <Input
                  aria-label={tx("Label for {p0}", { p0: name })}
                  value={fieldTitle(
                    name,
                    data.fields,
                    config.fieldLabels?.[name],
                  )}
                  onChange={(e) =>
                    set({
                      fieldLabels: {
                        ...config.fieldLabels,
                        [name]: e.target.value,
                      },
                    })
                  }
                />
              </Setting>
              {field?.type === "number" && (
                <Setting title={tx("Number format")}>
                  <SelectField
                    aria-label={tx("Format for {p0}", { p0: name })}
                    value={config.fieldFormats?.[name] ?? field.format}
                    onValueChange={(value) =>
                      set({
                        fieldFormats: {
                          ...config.fieldFormats,
                          [name]: value as FieldFormat,
                        },
                      })
                    }
                  >
                    {FIELD_FORMATS.map((format) => (
                      <option key={format} value={format}>
                        {label(format)}
                      </option>
                    ))}
                  </SelectField>
                </Setting>
              )}
            </div>
          );
        })}
      </fieldset>
      {isChart && (
        <fieldset className="space-y-2">
          <legend className="mb-2 text-xs font-semibold">
            {config.type === "pie" ? tx("Slice colors") : tx("Series colors")}
          </legend>
          {(config.type === "pie"
            ? [...new Set(data.rows.map((r) => String(r[config.x])))]
            : config.y
          ).map((key, i) => (
            <Setting
              key={key}
              title={fieldTitle(
                key,
                data.fields,
                config.fieldLabels?.[key],
                literals.has(key),
              )}
            >
              <Input
                type="color"
                aria-label={tx("Color for {p0}", { p0: key })}
                value={
                  config.seriesColors?.[key] ??
                  CHART_COLORS[i % CHART_COLORS.length]
                }
                onChange={(e) =>
                  set({
                    seriesColors: {
                      ...config.seriesColors,
                      [key]: e.target.value,
                    },
                  })
                }
              />
            </Setting>
          ))}
        </fieldset>
      )}
    </div>
  );
}
